import { FormEvent } from 'react'
import { Page, PageHeader, Section } from '@/components/page'
import { useForm } from '@inertiajs/react'
import { WizardGuide } from '@/components/layout/WizardGuide'
import { WizardLayout } from '../WizardLayout'
import { WizardActions } from '../WizardActions'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { useBasePath } from '@/lib/paths'
import { firstError } from '@/lib/utils'
import { ArrowRight, ShieldAlert } from 'lucide-react'
import { Tip } from '@/components/sketch/Tip'
import type { WizardFormationRef } from '@/types'

type Signer = {
  name: string
  capacity: string
  capacity_label: string
  represented_name: string
  represented_principal_id: string
}

type Props = {
  formation: WizardFormationRef
  signers: Signer[]
  consent: { version: string; text: string } | null
  ready: boolean
}

export default function SignatureEdit({ formation, signers, consent, ready }: Props) {
  const basePath = useBasePath()
  const form = useForm({
    typed_name: '',
    consented: false,
    represented_principal_id: signers[0]?.represented_principal_id ?? '',
  })

  const selectedSigner = signers.find((s) => s.represented_principal_id === form.data.represented_principal_id) ?? signers[0]
  const canContinue = ready && signers.length > 0 && form.data.consented && form.data.typed_name.trim().length > 0
  const baseError = firstError((form.errors as Record<string, string | undefined>).base)

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    form.patch(`${basePath}/formations/${formation.id}/wizard/signature`)
  }

  return (
    <WizardLayout
      title="Authorize your application"
      currentStep="signature"
      formationId={formation.id}
      resumeStep={formation.current_wizard_step}
      breadcrumbs={[{ label: 'Businesses', href: 'businesses' }, { label: 'Authorize your application' }]}
      guide={
        <WizardGuide title="Authorize your application">
          <p>
            You're confirming the facts in your application and authorizing Corppy to prepare and submit your company
            formation after you pay.
          </p>
          <p>
            This authorization does <strong>not</strong> execute the governance and tax documents. Each document follows
            its own signing requirements.
          </p>
        </WizardGuide>
      }
      tips={
        <Tip seed="signature-authorization-only">
          Typing your name here authorizes the application only. Your governing and tax documents are not signed yet.
        </Tip>
      }
    >
      <Page>
        <PageHeader
          title={<>Authorize your application</>}
          subtitle={<>Confirm the details below, then type your name to authorize the application.</>}
        />

        {baseError && (
          <div role="alert" className="rounded-lg border border-destructive/40 bg-muted/40 p-4 text-sm text-destructive">
            {baseError}
          </div>
        )}

        {!ready && (
          <div role="alert" className="rounded-lg border border-destructive/40 bg-muted/40 p-4 text-sm text-destructive">
            You can't authorize the application yet. Finish the earlier steps first.
          </div>
        )}

        {ready && signers.length > 0 && consent && (
          <form onSubmit={handleSubmit} className="space-y-6">
            <Section title="Who is authorizing">
              {signers.length > 1 ? (
                <RadioGroup
                  value={form.data.represented_principal_id}
                  onValueChange={(value) => form.setData('represented_principal_id', value)}
                  className="space-y-3"
                >
                  {signers.map((signer) => (
                    <label
                      key={signer.represented_principal_id}
                      className="flex items-start gap-3 rounded-lg border p-3 text-sm has-[[data-state=checked]]:border-primary"
                    >
                      <RadioGroupItem
                        value={signer.represented_principal_id}
                        id={`signer-${signer.represented_principal_id}`}
                        className="mt-0.5"
                      />
                      <span className="flex-1">
                        <span className="block font-medium">{signer.name}</span>
                        <span className="block text-muted-foreground">{signer.capacity_label}</span>
                        {signer.represented_name && signer.represented_name !== signer.name && (
                          <span className="block text-muted-foreground">On behalf of {signer.represented_name}</span>
                        )}
                      </span>
                    </label>
                  ))}
                </RadioGroup>
              ) : (
                <dl className="grid gap-3 text-sm">
                  <div className="flex justify-between gap-4">
                    <dt className="text-muted-foreground">Signer</dt>
                    <dd className="text-right font-medium">{selectedSigner?.name}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-muted-foreground">Capacity</dt>
                    <dd className="text-right font-medium">{selectedSigner?.capacity_label}</dd>
                  </div>
                  {selectedSigner?.represented_name && selectedSigner.represented_name !== selectedSigner.name && (
                    <div className="flex justify-between gap-4">
                      <dt className="text-muted-foreground">On behalf of</dt>
                      <dd className="text-right font-medium">{selectedSigner.represented_name}</dd>
                    </div>
                  )}
                </dl>
              )}
            </Section>

            <div
              role="note"
              className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-muted/40 p-4"
            >
              <ShieldAlert className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden="true" />
              <div className="text-sm text-foreground">
                <p className="font-medium">This authorizes the application only</p>
                <p className="mt-1 text-muted-foreground">
                  Your governance and tax documents follow their own execution and filing requirements.
                </p>
              </div>
            </div>

            <Section title="Authorization">
              <p className="text-sm text-foreground">{consent.text}</p>
              <p className="mt-1 text-xs text-muted-foreground">Consent version {consent.version}</p>
            </Section>

            <div className="space-y-1.5">
              <Label htmlFor="typed_name">Type your full name</Label>
              <Input
                id="typed_name"
                value={form.data.typed_name}
                onChange={(e) => form.setData('typed_name', e.target.value)}
                autoComplete="name"
                aria-invalid={!!form.errors.typed_name}
              />
              {form.errors.typed_name && <p className="text-xs text-destructive" role="alert">{form.errors.typed_name}</p>}
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                id="consented"
                checked={form.data.consented}
                onCheckedChange={(checked) => form.setData('consented', checked === true)}
              />
              <Label htmlFor="consented" className="text-sm text-foreground">
                I confirm the information is accurate and authorize Corppy to prepare and submit this formation after
                payment. I understand this authorization does not execute my governance or tax documents.
              </Label>
            </div>

            <WizardActions
              currentStep="signature"
              formationId={formation.id}
              disabled={form.processing}
              className="flex flex-wrap items-center gap-2"
            >
              <Button type="submit" disabled={form.processing || !canContinue}>
                {form.processing ? 'Authorizing…' : 'Continue'}
                <ArrowRight className="size-4" />
              </Button>
            </WizardActions>
          </form>
        )}
        {(!ready || signers.length === 0 || !consent) && (
          <WizardActions currentStep="signature" formationId={formation.id} />
        )}
      </Page>
    </WizardLayout>
  )
}
